import React, { Suspense } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { KambiSportName } from '@mobi/api-types/kambi/sports'
import { getKambiJerseysForEventQuery } from '@mobi/sports-offering/helpers/queries'
import { getPlayerJersey } from '../../helpers/playerJersey'
import type { DefaultIconType, SportIconSize } from '../../types'
import { DefaultIcon } from './Components/Common/DefaultIcon'
import { PlayerJerseyIcon } from './Components/PlayerJersey'
import { mappedSportsWithNationalFlags } from './Components/index'

export const TeamIcon: React.FC<{
  size: SportIconSize
  participantId: number | null | undefined
  sportName: KambiSportName | undefined
  defaultIconType?: DefaultIconType
  playerParticipantId?: number
  eventId?: number
}> = React.memo(
  ({ participantId, size, sportName, defaultIconType, playerParticipantId, eventId }) => {
    const { data: eventJerseys } = useQuery(
      getKambiJerseysForEventQuery(eventId || 0, Boolean(eventId && playerParticipantId))
    )

    const playerJersey = playerParticipantId
      ? getPlayerJersey(eventJerseys, playerParticipantId)
      : undefined

    const playerJerseyUrl = playerJersey?.jersey_icon?.url
    const playerJerseyNumber = playerJersey?.number?.jersey_number ?? playerJersey?.jersey_number
    const playerJerseyNumberColor = playerJersey?.number?.jersey_number_color

    if (playerJerseyUrl && playerJerseyNumber) {
      return (
        <PlayerJerseyIcon
          url={playerJerseyUrl}
          number={playerJerseyNumber}
          numberColor={playerJerseyNumberColor}
        />
      )
    }

    const MappedIcon = participantId && sportName && mappedSportsWithNationalFlags[sportName]

    if (MappedIcon) {
      return (
        <Suspense fallback={null}>
          <MappedIcon
            size={size}
            participantId={participantId}
            sportName={sportName}
            defaultIconType={defaultIconType ?? null}
          />
        </Suspense>
      )
    }

    return (
      <DefaultIcon
        sportName={sportName}
        defaultIconType={defaultIconType ?? (sportName ? 'sport-icon' : null)}
        size={size}
      />
    )
  },
  (prevProps, nextProps) =>
    prevProps.participantId === nextProps.participantId &&
    prevProps.sportName === nextProps.sportName &&
    prevProps.size === nextProps.size &&
    prevProps.defaultIconType === nextProps.defaultIconType &&
    prevProps.playerParticipantId === nextProps.playerParticipantId &&
    prevProps.eventId === nextProps.eventId
)

TeamIcon.displayName = 'TeamIcon'
